import { Color, Spacing } from '@persona-id/design-tokens';
import { TransferInIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Pagination from 'dashboard/components/Pagination';
import TableLayout, { TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { StickyTD, StickyTH } from 'dashboard/layouts/TableLayout/TableLayout';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { Importer } from 'dashboard/models/importer';
import { ImportedRow, ImporterRun, RunData } from 'dashboard/models/importer-run';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker/Sticker';

const StyledCardTitle = styled(Card.Title)`
  padding-bottom: ${Spacing[1]};
  margin-bottom: ${Spacing[0]};
  border-bottom: 1px solid ${Color.CoreSlate100};
`;

const StyledTableTR = styled.tr`
  // default background color is white and not transparent
  background-color: ${({ theme }) => theme.color.primary.white};
`;

const StyledAccountLink = styled(Link)`
  text-decoration: underline;
`;

export interface ImporterRunSuccessesTableProps {
  importerRun: ImporterRun;
  importer: Importer;
  handleDownloadCSV: () => void;
}

const ImporterRunSuccessesTable = ({
  importerRun,
  importer,
  handleDownloadCSV,
}: ImporterRunSuccessesTableProps) => {
  const runData: RunData = importerRun.attributes['run-data'];
  const successfulRows = importerRun.attributes['success-preview'];
  const columnNames = runData['column-names'] || [];

  const [secondColumnLeft, setSecondColumnLeft] = useState(0);
  const firstColumnRef = useCallback((node) => {
    if (!node) return;
    const resizeObserver = new ResizeObserver(() => {
      setSecondColumnLeft(node.getBoundingClientRect().width);
    });
    resizeObserver.observe(node);
  }, []);

  const [thirdColumnLeft, setThirdColumnLeft] = useState(0);
  const secondColumnRef = useCallback(
    (node) => {
      if (!node) return;
      const resizeObserver = new ResizeObserver(() => {
        setThirdColumnLeft(node.getBoundingClientRect().width + secondColumnLeft);
      });
      resizeObserver.observe(node);
    },
    [secondColumnLeft],
  );

  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsToDisplay = successfulRows.slice(pageSize * (currentPage - 1), pageSize * currentPage);

  return successfulRows.length > 0 ? (
    <>
      <StyledCardTitle
        actions={
          <Button
            disabled={!successfulRows.length}
            variant='basic'
            prefixIcon={<TransferInIcon />}
            onClick={handleDownloadCSV}
          >
            Download Summary
          </Button>
        }
      >
        Successful
        <Sticker variant='grey'>{runData['success-count']}</Sticker>
      </StyledCardTitle>
      {runData['success-count'] > successfulRows.length &&
        `Preview of the first ${successfulRows.length} rows. Download summary for all successes.`}
      <TableLayout
        showHeader
        containerMaxHeight={500}
        isEmpty={!successfulRows.length}
        nullState={null}
        headerTR={
          <>
            <StickyTH left={0} ref={firstColumnRef}>
              {importer.attributes['pipeline-config'].destination === 'Account'
                ? 'Account ID'
                : 'Transaction ID'}
            </StickyTH>
            <StickyTH left={secondColumnLeft} ref={secondColumnRef}>
              Row
            </StickyTH>
            <StickyTH left={thirdColumnLeft} hasRightBorder>
              Write Action
            </StickyTH>
            {columnNames.map((columnName) => (
              <TableTH key={`importer_success_header_row__${columnName}`}>{columnName}</TableTH>
            ))}
          </>
        }
      >
        {rowsToDisplay.map((row: ImportedRow) => {
          return (
            <StyledTableTR key={row['row-number']}>
              <StickyTD left={0}>
                <StyledAccountLink
                  to={`/dashboard/${
                    importer.attributes['pipeline-config'].destination === 'Account'
                      ? 'accounts'
                      : 'transactions'
                  }/${row.token}`}
                >
                  {row.token}
                </StyledAccountLink>
              </StickyTD>
              <StickyTD left={secondColumnLeft}>{row['row-number']}</StickyTD>
              <StickyTD
                left={thirdColumnLeft}
                hasRightBorder
                key='importer_success_row__row_action'
              >
                <Sticker variant='grey'>{titleCase(row.action)}</Sticker>
              </StickyTD>
              {columnNames.map((columnName) => (
                <TableTD key={`importer_success_row__${columnName}__${row['row-number']}`}>
                  {row.row[columnName]}
                </TableTD>
              ))}
            </StyledTableTR>
          );
        })}
      </TableLayout>
      <Pagination
        pageSize={pageSize}
        paginationParams={{
          'current-page': currentPage,
          'total-count': successfulRows.length,
          'total-pages': Math.ceil(successfulRows.length / pageSize),
        }}
        handlePageSizeClick={(newPageSize) => {
          setPageSize(newPageSize);
          const maxPage = Math.ceil(successfulRows.length / newPageSize);
          setCurrentPage(Math.min(maxPage, currentPage));
        }}
        handlePageLinkClick={setCurrentPage}
      />
    </>
  ) : null;
};

export default ImporterRunSuccessesTable;
